import * as apiPublic from "@api/public";
import AnnuncioPreview from '@templates/annunci/AnnuncioPreview';
import SponsorPreview from "@templates/pubblicita/SponsorPreview";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import SearchInput from "@components/searchInput";
import SelectCategory from "@templates/selectCategory";
import SelectProvince from "@templates/selectProvince";
import styles from "./Search.module.css";

const Search = () => {
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [filterProvince, setFilterProvince] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  // const [selectedAnnuncio, setSelectedAnnuncio] = useState(null);

  // const user = useSelector(selectUser);
  // const { navigate } = useNavigator();
  // const { openModal } = useModals();

  // const userIconLinkRoute = useMemo(() => (
  //   user.isLogged ? ROUTES.MY_ACCOUNT : ROUTES.LOGIN
  // ), [user.isLogged]);

  // const onAnnuncioClick = useCallback((annuncio) => {
  //   openModal({
  //     title: annuncio.description,
  //     children: (
  //       <Annuncio initialAnnuncio={annuncio} />
  //     ),
  //   });
  // }, [openModal]);

  const annunciList = useMemo(() => (
    loading
      ? [...Array(20)].map((_, i) => (
        <AnnuncioPreview key={i} loading />
      ))
      : advertisements.flatMap((advertisement, index) => {
        const sponsor = sponsors.length > 0 && sponsors.length > Math.floor(index / 2) ? sponsors[Math.floor(index / 2)] : null;

        const items = [
          <AnnuncioPreview
            key={`announcement-${advertisement.id}`}
            annuncio={advertisement}
            vertical={!!advertisement.isFeatured}
            desktopDoubleHeight={!!advertisement.isFeatured}
          />,
        ];

        if (sponsor && (index + 1) % 2 === 0) {
          items.push(
            <SponsorPreview key={`sponsor-${sponsor.id}-${index}`} sponsor={sponsor} slot={index} />
          );
        }

        return items;
      })
  ), [loading, advertisements, sponsors]);

  // const loadAdvertisements = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const data = await apiPublic.GetFeaturedAdvertisements();
  //     setAdvertisements(data);
  //   } catch {
  //     setAdvertisements([]);
  //   }
  //   setLoading(false);
  // }, []);

  const filters = useMemo(() => {
    const trimmedText = filterText.trim();
    const hasCategory = filterCategoryId !== null && filterCategoryId !== undefined && filterCategoryId !== "";
    const parsedCategoryId = hasCategory ? Number(filterCategoryId) : null;
    const categoryId = Number.isNaN(parsedCategoryId) ? null : parsedCategoryId;
    const province = filterProvince || null;

    return {
      text: trimmedText,
      categoryId,
      province,
      hasFilters: Boolean(trimmedText) || categoryId !== null || Boolean(province),
    };
  }, [filterText, filterCategoryId, filterProvince]);

  const loadFeaturedAdvertisements = useCallback(async () => {
    return await apiPublic.GetFeaturedAdvertisements();
  }, []);

  const loadSponsors = useCallback(async (categoryId) => {
    return await apiPublic.GetSponsors({ categoryId });
  }, []);

  const searchAdvertisements = useCallback(async ({ text, categoryId, province }) => {
    const body = {
      searchText: text || null,
      province: province || null,
      page: 1,
    };

    if (categoryId !== null) {
      body.categoryId = categoryId;
    }

    return await apiPublic.SearchAdvertisements(body);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get("query") ?? "";
    const category = params.get("category") ?? "";
    const province = params.get("province") ?? "";

    setFilterText(query);
    setFilterCategoryId(category || null);
    setFilterProvince(province || null);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if(filterText)
      params.set("query", filterText);
    if(filterCategoryId)
      params.set("category",filterCategoryId !== null && filterCategoryId !== undefined ? String(filterCategoryId) : "");
    if(filterProvince)
      params.set("province", filterProvince || "");

    const newSearch = params.size > 0 ? `?${params.toString()}` : ``;
    
    if (newSearch !== window.location.search) {
      const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.history.replaceState({}, "", newUrl);
    }
    
  }, [filterText, filterCategoryId, filterProvince]);

  useEffect(() => {
    let isActive = true;

    if (filters.hasFilters) {
      setLoading(true);
      const debounced = setTimeout(() => {
        searchAdvertisements(filters)
          .then((data) => {
            if (!isActive) {
              return;
            }
            setAdvertisements(data);
          })
          .catch(() => {
            if (!isActive) {
              return;
            }
            setAdvertisements([]);
          })
          .finally(() => {
            if (!isActive) {
              return;
            }
            setLoading(false);
          });
      }, 500);

      return () => {
        isActive = false;
        clearTimeout(debounced);
      };
    }

    setLoading(true);
    loadFeaturedAdvertisements()
      .then((data) => {
        if (!isActive) {
          return;
        }
        setAdvertisements(data);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setAdvertisements([]);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [filters, loadFeaturedAdvertisements, searchAdvertisements]);

  useEffect(() => {
    let isActive = true;
    const resolvedCategoryId = filters.categoryId ?? 0;

    loadSponsors(resolvedCategoryId)
      .then((data) => {
        if (!isActive) {
          return;
        }
        setSponsors(data || []);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setSponsors([]);
      });

    return () => {
      isActive = false;
    };
  }, [filters.categoryId, loadSponsors]);

  return (
    <>
      <br></br>
      <div className='row'>
        <div className='col'>
          <SearchInput value={filterText} setValue={setFilterText} autoFocus />
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <SelectCategory
            value={filterCategoryId}
            setValue={setFilterCategoryId}
            disabled={loading}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <SelectProvince
            label="Provincia"
            value={filterProvince}
            setValue={setFilterProvince}
            disabled={loading}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <span className='page-title'>Risultati ricerca</span>
        </div>
      </div>
      <div className='row'>
        {annunciList.length === 0 ? (
          <div className='col col-flex-center'>
            <span style={{ marginTop: 20 }}>Nessun annuncio trovato</span>
          </div>
        ) : (
          <div className='col'>
            <div className={styles.wrapperAnnunci}>
              {annunciList}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Search;
